import { useEffect, useRef, useState, useCallback } from "react"
import { gsap } from "gsap"

// Import des images des chaînes
import AppleTV from "../assets/canal/Apple_TV.png"
import BeinSports from "../assets/canal/Bein_group.png"
import DAZN from "../assets/canal/DAZN.png"
import DisneyPlus from "../assets/canal/Disney+.png"
import HBOMax from "../assets/canal/HBO_Max.png"
import Hulu from "../assets/canal/Hulu.png"
import Netflix from "../assets/canal/Netflix.png"
import PlutoTV from "../assets/canal/Pluto_TV.png"
import PrimeVideo from "../assets/canal/Prime_Video.png"
import Antena3 from "../assets/canal/Antena_3.png"
import Cuatro from "../assets/canal/Cuatro.png"
import ESPN from "../assets/canal/ESPN.png"
import Filmbox from "../assets/canal/Filmbox.png"
import MovistarPlus from "../assets/canal/Movistar_Plus.png"
import RakutenTV from "../assets/canal/Rakuten_TV.png"
import SkyShowtime from "../assets/canal/SkyShowtime.png"
import RTVE from "../assets/canal/rtve.png"

const channels = [
  { name: "Netflix", logo: Netflix, alt: "Netflix logo" },
  { name: "Prime Video", logo: PrimeVideo, alt: "Prime Video logo" },
  { name: "Disney+", logo: DisneyPlus, alt: "Disney+ logo" },
  { name: "Bein Sports", logo: BeinSports, alt: "Bein Sports logo" },
  { name: "DAZN", logo: DAZN, alt: "DAZN logo" },
  { name: "Apple TV", logo: AppleTV, alt: "Apple TV logo" },
  { name: "HBO Max", logo: HBOMax, alt: "HBO Max logo" },
  { name: "Hulu", logo: Hulu, alt: "Hulu logo" },
  { name: "Pluto TV", logo: PlutoTV, alt: "Pluto TV logo" },
  { name: "Antena 3", logo: Antena3, alt: "Antena 3 logo" },
  { name: "Cuatro", logo: Cuatro, alt: "Cuatro logo" },
  { name: "ESPN", logo: ESPN, alt: "ESPN logo" },
  { name: "Filmbox", logo: Filmbox, alt: "Filmbox logo" },
  { name: "Movistar Plus", logo: MovistarPlus, alt: "Movistar Plus logo" },
  { name: "Rakuten TV", logo: RakutenTV, alt: "Rakuten TV logo" },
  { name: "SkyShowtime", logo: SkyShowtime, alt: "SkyShowtime logo" },
  { name: "RTVE", logo: RTVE, alt: "RTVE logo" },
]

// Directions d'animation pour chaque position
const animationDirections = ["left", "bottom", "right", "top", "right", "bottom"]

// Configuration des breakpoints responsive
const getResponsiveConfig = () => {
  if (typeof window === 'undefined') return { itemsPerPage: 6, gridCols: 'grid-cols-6' }
  
  const width = window.innerWidth
  if (width >= 1024) return { itemsPerPage: 6, gridCols: 'grid-cols-6' }
  if (width >= 768) return { itemsPerPage: 5, gridCols: 'grid-cols-5' }
  return { itemsPerPage: 3, gridCols: 'grid-cols-3' }
}

export default function ChannelCarousel() {
  const containerRef = useRef(null)
  const logoRefs = useRef([])
  const masterTimelineRef = useRef(null)
  const currentIndexRef = useRef(0)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [responsiveConfig, setResponsiveConfig] = useState(getResponsiveConfig())

  // Gérer la responsivité
  useEffect(() => {
    const handleResize = () => {
      setResponsiveConfig(getResponsiveConfig())
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Obtenir les chaînes visibles
  const getVisibleChannels = useCallback(() => {
    const { itemsPerPage } = responsiveConfig
    return Array.from({ length: itemsPerPage }, (_, i) => {
      const index = (currentIndexRef.current + i) % channels.length
      return channels[index]
    })
  }, [responsiveConfig])

  // Animation GSAP pour un logo individuel
  const animateLogo = useCallback((logoElement, direction, isExit = false) => {
    if (!logoElement) return

    const duration = 0.6
    const ease = "power2.inOut"

    // Configuration des animations selon la direction
    const animations = {
      left: {
        enter: { x: -200, opacity: 0 },
        exit: { x: 200, opacity: 0 }
      },
      right: {
        enter: { x: 200, opacity: 0 },
        exit: { x: -200, opacity: 0 }
      },
      top: {
        enter: { y: -200, opacity: 0 },
        exit: { y: 200, opacity: 0 }
      },
      bottom: {
        enter: { y: 200, opacity: 0 },
        exit: { y: -200, opacity: 0 }
      }
    }

    const config = animations[direction]
    if (!config) return

    const props = isExit ? config.exit : config.enter
    const toProps = isExit ? { x: 0, y: 0, opacity: 1 } : { x: 0, y: 0, opacity: 1 }

    if (isExit) {
      return gsap.to(logoElement, {
        ...props,
        duration,
        ease,
        willChange: "transform, opacity"
      })
    } else {
      return gsap.fromTo(logoElement, props, {
        ...toProps,
        duration,
        ease,
        willChange: "transform, opacity"
      })
    }
  }, [])

  // Animation principale avec GSAP
  const startGSAPAnimation = useCallback(() => {
    if (!containerRef.current) return

    // Nettoyer la timeline précédente
    if (masterTimelineRef.current) {
      masterTimelineRef.current.kill()
    }

    const { itemsPerPage } = responsiveConfig
    const nextIndex = (currentIndexRef.current + itemsPerPage) % channels.length

    // Créer la timeline maître
    const masterTimeline = gsap.timeline({
      onComplete: () => {
        // Mettre à jour l'index après l'animation de sortie
        currentIndexRef.current = nextIndex
        setCurrentIndex(nextIndex)
        // Programmer la prochaine animation après 3 secondes (délai uniforme)
        setTimeout(() => {
          startGSAPAnimation()
        }, 3000)
      }
    })

    // Calculer le stagger pour que l'animation totale dure le même temps
    // Durée totale souhaitée : 1.5 secondes pour toutes les animations
    const totalAnimationDuration = 1.5
    const singleAnimationDuration = 0.6
    const staggerDelay = (totalAnimationDuration - singleAnimationDuration) / Math.max(1, itemsPerPage - 1)

    // Animer chaque logo avec stagger (sortie)
    for (let i = 0; i < itemsPerPage; i++) {
      const logoElement = logoRefs.current[i]
      const direction = animationDirections[i] || animationDirections[i % animationDirections.length]
      
      if (logoElement) {
        // Animation de sortie avec stagger calculé
        masterTimeline.add(animateLogo(logoElement, direction, true), i * staggerDelay)
      }
    }

    masterTimelineRef.current = masterTimeline
  }, [responsiveConfig, animateLogo])

  // Animation d'entrée quand les nouveaux logos sont chargés
  useEffect(() => {
    const { itemsPerPage } = responsiveConfig
    const timeline = gsap.timeline()

    // Même calcul de stagger pour l'entrée
    const totalAnimationDuration = 1.5
    const singleAnimationDuration = 0.6
    const staggerDelay = (totalAnimationDuration - singleAnimationDuration) / Math.max(1, itemsPerPage - 1)

    for (let i = 0; i < itemsPerPage; i++) {
      const logoElement = logoRefs.current[i]
      const direction = animationDirections[i] || animationDirections[i % animationDirections.length]
      
      if (logoElement) {
        timeline.add(animateLogo(logoElement, direction, false), i * staggerDelay)
      }
    }

    return () => timeline.kill()
  }, [currentIndex, responsiveConfig, animateLogo])

  // Démarrer les animations
  useEffect(() => {
    // Délai initial pour GSAP
    const initialDelay = setTimeout(startGSAPAnimation, 2000)
    return () => clearTimeout(initialDelay)
  }, [startGSAPAnimation])

  // Cleanup
  useEffect(() => {
    return () => {
      if (masterTimelineRef.current) {
        masterTimelineRef.current.kill()
      }
    }
  }, [])

  const visibleChannels = getVisibleChannels()

  return (
    <section 
      className="relative flex items-center justify-center overflow-hidden"
      ref={containerRef}
    >
      <div className="w-full">
        
        <div className="relative overflow-hidden">
          <div className={`grid grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-8 md:gap-12 lg:gap-16`}>
            {visibleChannels.map((channel, index) => {
              const isLastOnMobile = index >= 3
              const isLastOnTablet = index >= 5
              
              return (
              <div
                key={`${channel.name}-${currentIndex}-${index}`}
                  className={`
                    flex items-center justify-center min-h-[60px] 
                    ${isLastOnMobile ? 'hidden md:flex' : ''} 
                    ${isLastOnTablet ? 'hidden lg:flex' : ''}
                  `}
                  style={{
                    willChange: 'transform, opacity'
                  }}
                >
                  <div
                    ref={el => logoRefs.current[index] = el}
                    className="flex items-center justify-center w-full h-full"
                    aria-label={channel.alt}
                    role="img"
                  >
                    <img
                      src={channel.logo}
                      alt={channel.alt}
                      className="max-w-26 max-h-12 object-contain filter brightness-0 invert transition-all duration-300"
                      loading="lazy"
                    />
                  </div>
              </div>
              )
            })}
          </div>
        </div>
      </div>
    </section>
  )
}